import { gql, useQuery, type ErrorPolicy, type WatchQueryFetchPolicy } from '@apollo/client';

import { NexoyaQuery, NexoyaQueryIntegrationsArgs } from '../../types/types';
import { useTeam } from '../../context/TeamProvider';

import { separateLoading } from '../../utils/graphql';
import { maybe } from '../../utils/object';

import { USER_META_FRAGMENT } from '../user/fragments';
import { INTEGRATION_META_FRAGMENT } from './fragments';

const INTEGRATIONS_QUERY = gql`
  query integrations(
    $team_id: Int!
    $withUser: Boolean!
    $withConnection: Boolean!
    $withFilters: Boolean!
    $type: String
  ) {
    integrations(team_id: $team_id, filter: { type: $type }) {
      ...integrationMeta
      connectionUrl @include(if: $withConnection)
      connected @include(if: $withConnection)
      status @include(if: $withConnection)
      lastSyncAt @include(if: $withConnection)
      type @include(if: $withConnection)
      hasFilter @include(if: $withConnection)
      fields @include(if: $withConnection)
      filterOptions @include(if: $withFilters) {
        filterName
        filterList {
          id
          itemInfo
          selected
        }
      }
      provider {
        provider_id
        logo
        name
        description
        category
        showInToplist
        manualConnect @include(if: $withConnection) {
          fields {
            name
            target
            label
            required
            secret
          }
        }
      }
      user @include(if: $withUser) {
        ...userMeta
      }
    }
  }
  ${USER_META_FRAGMENT}
  ${INTEGRATION_META_FRAGMENT}
`;
type Options = {
  withUser?: boolean;
  withConnection?: boolean;
  withFilters?: boolean;
  type?: 'auto' | 'manual';
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  errorPolicy?: ErrorPolicy;
  pollInterval?: number;
  skipPollAttempt?: () => boolean;
  context?: Record<string, unknown>;
};

function useIntegrationsQuery({
  withUser = false,
  withConnection = false,
  withFilters = false,
  type = null,
  skip = false,
  // The @include flags are variables, not field arguments, so every caller writes the same
  // `ROOT_QUERY.integrations` slot whatever it asked for. Anyone running alongside
  // Settings › Integrations must pass 'no-cache'.
  fetchPolicy = 'network-only',
  errorPolicy,
  pollInterval = 0,
  skipPollAttempt,
  context,
}: Options = {}): any {
  const { teamId } = useTeam();
  const query = useQuery<
    Pick<NexoyaQuery, 'integrations'>,
    NexoyaQueryIntegrationsArgs & { withUser?: boolean; withConnection?: boolean; withFilters?: boolean; type?: string }
  >(INTEGRATIONS_QUERY, {
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy,
    errorPolicy,
    pollInterval,
    skipPollAttempt,
    context,
    variables: {
      team_id: teamId,
      withUser,
      withConnection,
      withFilters,
      ...maybe('type', type),
    },
  });
  return { ...separateLoading(query) };
}

export { INTEGRATIONS_QUERY, useIntegrationsQuery };
